import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Send as SendIcon,
  Person as UserIcon,
  SmartToy as AIIcon,
  Build as BuildIcon,
  HelpOutline as HelpOutlineIcon,
  Refresh as RefreshIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Description as FileIcon,
  Image as ImageIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  LocalOffer as TagIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import api from '../../api/axios';
import API_BASE_URL from '../../api/config';
import ThinkingBubble from './ThinkingBubble';

/**
 * Chat Panel Component
 * Handles chat interface with AI assistant
 */
// Accepted file types for sample uploads
const ACCEPTED_FILE_TYPES = '.pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.doc,.docx,.txt';
const MAX_FILES = 5;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const ChatPanel = ({ session, currentFlow, onFlowGenerated, onSessionNameChange, onMessageSend }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [mode, setMode] = useState('agent'); // 'agent' or 'ask'
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  // Steps the user tagged as suspected problem steps for this message
  const [taggedSteps, setTaggedSteps] = useState([]);
  const [tagMenuAnchor, setTagMenuAnchor] = useState(null);
  const [tagMenuFromAt, setTagMenuFromAt] = useState(false);
  const messagesEndRef = useRef(null);
  const textFieldRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load session messages
  useEffect(() => {
    if (session) {
      loadMessages();
    }
  }, [session]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent, loading]);

  const loadMessages = async () => {
    try {
      const response = await api.get(`/ai-flow-builder/session/${session.sessionId}`);
      setMessages(response.data.session.messages || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /**
   * Try to handle a step modification request directly
   * This is more reliable than asking the AI to output entire flow JSON
   */
  const tryStepModification = async (message) => {
    console.log('🔧 [tryStepModification] Starting with message:', message.substring(0, 50));
    const lowerMessage = message.toLowerCase();
    
    // Detect step modification patterns
    const stepPatterns = [
      { pattern: /end\s*step/i, stepType: 'end' },
      { pattern: /final\s*step/i, stepType: 'end' },
      { pattern: /last\s*step/i, stepType: 'end' },
      { pattern: /display\s*(step|final)/i, stepType: 'end' },
      { pattern: /start\s*step/i, stepType: 'smart_start' },
      { pattern: /first\s*step/i, stepType: 'smart_start' },
      { pattern: /collect\s*input/i, stepType: 'advanced_collect_input' },
      { pattern: /review\s*step/i, stepType: 'advanced_collect_input' },
      { pattern: /extraction\s*step/i, stepType: 'ai_extraction' },
      { pattern: /variable\s*operations?/i, stepType: 'variable_operations' },
      { pattern: /custom\s*code/i, stepType: 'custom_code' },
      { pattern: /javascript\s*(step|node)/i, stepType: 'custom_code' },
    ];

    // Also detect modification intent - more flexible patterns
    const modificationPatterns = [
      // Header modifications
      /add\s+\w+\s+(to\s+)?(the\s+)?header/i,  // "add X to the header"
      /add\s*(a\s*)?(header|column|field)/i,    // "add a header"
      /header.*add/i,                            // "header ... add"
      /(in|to)\s*(the\s+)?header/i,             // "in the header" or "to the header"
      /display\s+\w+\s+(in|on)\s*(the\s+)?header/i, // "display X in header"
      /show\s+\w+\s+(in|on)\s*(the\s+)?header/i,    // "show X in header"
      /header\s*(is\s*)?(still\s*)?(empty|blank|missing)/i, // "header is empty"
      
      // General step modifications
      /change\s*(the\s*)?(title|name|label|prompt|hint)/i,
      /update\s*(the\s*)?(config|configuration|step|prompt|extraction)/i,
      /modify\s*(the\s*)?(step|config|prompt)/i,
      /fix\s*(the\s*)?(header|step|prompt|extraction|variable|matching|combine|enrich|join)/i,
      /^fix\b/i, // "Fix the variable_operations ..." style repair requests
      
      // Enhance/Improve patterns
      /enhance\s*(the\s*)?(prompt|step|extraction|hint|instructions?)/i,
      /improve\s*(the\s*)?(prompt|step|extraction|hint|instructions?)/i,
      /make\s*(the\s*)?(prompt|extraction)\s*(better|more\s+detailed)/i,
      
      // Display patterns
      /show\s*(the\s*)?(purchaseorder|po\s*number)/i,
      /display\s*(the\s*)?(purchaseorder|variable)/i,
      
      // Include/Add examples
      /include\s*(examples?|hints?)/i,           // "include examples"
      /add\s*(examples?|hints?|instructions?)/i, // "add examples"
      
      // Step-specific with quotes (step name mentions)
      /"[^"]+"\s*step/i,                         // "Step Name" step
      /step\s*"[^"]+"/i,                         // step "Step Name"
      /in\s*(the\s*)?"[^"]+"/i,                  // in the "Step Name"
    ];

    const hasModificationIntent = modificationPatterns.some(p => p.test(message));
    console.log('🔧 [tryStepModification] Modification intent detected:', hasModificationIntent);
    
    if (!hasModificationIntent) {
      console.log('🔧 [tryStepModification] No modification intent, returning null');
      return null;
    }

    // Find which step to modify
    let targetStep = null;
    console.log('🔧 [tryStepModification] Looking for target step, available steps:', 
      currentFlow.steps.map(s => ({ name: s.name, type: s.type })));
    
    // First, check for step names in quotes (highest priority)
    const quotedStepMatch = message.match(/"([^"]+)"/);
    if (quotedStepMatch) {
      const quotedName = quotedStepMatch[1].toLowerCase();
      console.log('🔧 [tryStepModification] Looking for quoted step name:', quotedName);
      for (const step of currentFlow.steps) {
        if (step.name && step.name.toLowerCase().includes(quotedName)) {
          targetStep = step;
          console.log('🔧 [tryStepModification] Found step by quoted name:', targetStep.name);
          break;
        }
        // Also check if quoted name includes step name
        if (step.name && quotedName.includes(step.name.toLowerCase())) {
          targetStep = step;
          console.log('🔧 [tryStepModification] Found step by quoted name (reverse):', targetStep.name);
          break;
        }
      }
    }
    
    // Next, check step type patterns
    if (!targetStep) {
      for (const { pattern, stepType } of stepPatterns) {
        if (pattern.test(message)) {
          console.log('🔧 [tryStepModification] Pattern matched:', pattern.toString(), '-> stepType:', stepType);
          targetStep = currentFlow.steps.find(s => s.type === stepType);
          if (targetStep) {
            console.log('🔧 [tryStepModification] Found step by pattern:', targetStep.name);
          }
          break;
        }
      }
    }

    // Also check for step name mentions (case-insensitive partial match)
    if (!targetStep) {
      for (const step of currentFlow.steps) {
        if (step.name && lowerMessage.includes(step.name.toLowerCase())) {
          targetStep = step;
          console.log('🔧 [tryStepModification] Found step by name:', targetStep.name);
          break;
        }
      }
    }

    // Default to end step for display/header modifications
    if (!targetStep && (lowerMessage.includes('header') || lowerMessage.includes('display'))) {
      console.log('🔧 [tryStepModification] Using default: looking for end/collect_input step');
      targetStep = currentFlow.steps.find(s => s.type === 'end') ||
                   currentFlow.steps.find(s => s.type === 'advanced_collect_input');
    }

    if (!targetStep) {
      console.log('🔧 [tryStepModification] No target step identified in message');
      return null;
    }

    console.log('🔧 [tryStepModification] Detected step modification:', {
      step: targetStep.name,
      type: targetStep.type,
      stepId: targetStep.stepId || targetStep._id,
      request: message.substring(0, 50)
    });

    try {
      // Get the step ID - could be in different properties depending on source
      const stepIdentifier = targetStep.stepId || targetStep._id || targetStep.id;
      console.log('🔧 [tryStepModification] Calling /modify-step with stepId:', stepIdentifier);
      
      // Call the specialized modify-step endpoint
      const response = await api.post('/ai-flow-builder/modify-step', {
        sessionId: session.sessionId,
        stepId: stepIdentifier,
        stepConfig: targetStep.config || {},
        stepType: targetStep.type,
        modificationRequest: message,
        currentFlow: currentFlow
      });

      console.log('🔧 [tryStepModification] API response:', response.data);

      if (response.data.modifiedConfig) {
        console.log('🔧 [tryStepModification] Got modified config');
        console.log('🔧 [tryStepModification] savedToDatabase:', response.data.savedToDatabase);
        
        // The backend saved the config to the database
        // Dispatch an event to trigger a flow reload from the server
        // This is cleaner than trying to update local state manually
        if (response.data.savedToDatabase) {
          console.log('✅ [tryStepModification] Step saved by backend, triggering flow reload...');
          
          // Dispatch custom event to trigger flow data refresh
          window.dispatchEvent(new CustomEvent('flowStepModified', {
            detail: { 
              stepId: targetStep.stepId || targetStep._id || targetStep.id,
              stepName: targetStep.name,
              modifiedConfig: response.data.modifiedConfig
            }
          }));
        }

        return {
          success: true,
          message: `✅ **Step Modified: ${targetStep.name}**\n\n${response.data.explanation}\n\n**The step has been updated. Click on the step in the canvas to see the changes.**\n\nHere's what was updated:\n\`\`\`json\n${JSON.stringify(response.data.modifiedConfig, null, 2)}\n\`\`\``
        };
      }

      return null;
    } catch (error) {
      console.error('🔧 [Modify Step] Error:', error);
      // If modification fails, fall through to regular chat
      return null;
    }
  };

  const generateSessionTitle = (message) => {
    // Generate a concise title from the user's message (like Cursor does)
    const maxLength = 40;
    let title = message.trim();
    
    // Remove common prefixes
    title = title.replace(/^(please |can you |could you |i want to |i need to |help me )/i, '');
    
    // Capitalize first letter
    title = title.charAt(0).toUpperCase() + title.slice(1);
    
    // Truncate if too long
    if (title.length > maxLength) {
      title = title.substring(0, maxLength).trim() + '...';
    }
    
    return title;
  };

  const updateSessionName = async (name) => {
    try {
      await api.patch(`/ai-flow-builder/session/${session.sessionId}/name`, { name });
      
      // Notify parent component about the name change
      if (onSessionNameChange) {
        onSessionNameChange(name);
      }
    } catch (error) {
      console.error('Error updating session name:', error);
    }
  };

  // -------- Step tagging (suspected problem steps) --------

  /**
   * Steps available for tagging, from the live canvas flow. Includes every
   * identifier we have (id/_id/stepId) so the backend can match reliably.
   */
  const getTaggableSteps = () => {
    if (currentFlow?.steps?.length > 0) {
      return currentFlow.steps.map((s, i) => ({
        id: s.id || (s._id?.toString ? s._id.toString() : s._id) || s.stepId || s.tempId || null,
        stepId: s.stepId || null,
        name: s.name || `Step ${i + 1}`,
        type: s.type || 'unknown',
        stepNumber: i + 1
      }));
    }
    if (currentFlow?.nodes?.length > 0) {
      return currentFlow.nodes.map((n, i) => ({
        id: n.id,
        stepId: n.data?.step?.stepId || null,
        name: n.data?.step?.name || n.data?.label || `Step ${i + 1}`,
        type: n.data?.step?.type || n.type || 'unknown',
        stepNumber: i + 1
      }));
    }
    return [];
  };

  const isStepTagged = (step) =>
    taggedSteps.some(t => (t.id || t.name) === (step.id || step.name));

  const toggleTaggedStep = (step) => {
    setTaggedSteps(prev => {
      const key = step.id || step.name;
      const exists = prev.some(t => (t.id || t.name) === key);
      return exists ? prev.filter(t => (t.id || t.name) !== key) : [...prev, step];
    });
    // If the picker was opened by typing "@", consume that trigger character
    if (tagMenuFromAt) {
      setInputMessage(prev => prev.replace(/@$/, ''));
      setTagMenuFromAt(false);
    }
  };

  const openTagMenu = (anchorEl, fromAt = false) => {
    if (getTaggableSteps().length === 0) return;
    setTagMenuFromAt(fromAt);
    setTagMenuAnchor(anchorEl);
  };

  const closeTagMenu = () => {
    setTagMenuAnchor(null);
    setTagMenuFromAt(false);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    // Typing "@" at the end of the input opens the step picker
    if (value.endsWith('@') && !inputMessage.endsWith('@')) {
      openTagMenu(e.target, true);
    }
    setInputMessage(value);
  };

  // File attachment handlers
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    // Validate file count
    if (attachedFiles.length + files.length > MAX_FILES) {
      alert(`You can only attach up to ${MAX_FILES} files.`);
      return;
    }

    // Validate file sizes and add to list
    const validFiles = files.filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" exceeds the 20MB limit.`);
        return false;
      }
      return true;
    });

    setAttachedFiles(prev => [...prev, ...validFiles]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      return <ImageIcon fontSize="small" />;
    }
    if (['xlsx', 'xls', 'csv'].includes(ext)) {
      return <ExcelIcon fontSize="small" />;
    }
    if (ext === 'pdf') {
      return <PdfIcon fontSize="small" />;
    }
    return <FileIcon fontSize="small" />;
  };

  const uploadSampleFiles = async () => {
    if (attachedFiles.length === 0) return null;
    
    setUploadingFiles(true);
    try {
      const formData = new FormData();
      attachedFiles.forEach((file) => {
        formData.append('sampleFiles', file);
      });
      formData.append('sessionId', session.sessionId);
      
      const response = await api.post('/ai-flow-builder/upload-samples', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('📁 Sample files uploaded:', response.data);
      return response.data.processedFiles;
    } catch (error) {
      console.error('Error uploading sample files:', error);
      throw error;
    } finally {
      setUploadingFiles(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    const isFirstMessage = messages.length === 0;
    const filesToUpload = [...attachedFiles]; // Copy before clearing
    const tagsToSend = [...taggedSteps]; // Copy before clearing
    
    setInputMessage('');
    setAttachedFiles([]); // Clear attached files
    setTaggedSteps([]); // Clear tagged steps
    closeTagMenu();
    setLoading(true);

    // Show the user's message immediately so the thinking indicator can sit under it
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
      attachedFiles: filesToUpload.length > 0 ? filesToUpload.map(f => f.name) : null,
      metadata: tagsToSend.length > 0 ? { taggedSteps: tagsToSend.map(t => t.name) } : undefined
    }]);
    
    // Upload sample files first if any
    let uploadedFileInfo = null;
    if (filesToUpload.length > 0) {
      try {
        // Create temp FormData for upload
        const formData = new FormData();
        filesToUpload.forEach((file) => {
          formData.append('sampleFiles', file);
        });
        formData.append('sessionId', session.sessionId);
        
        const uploadResponse = await api.post('/ai-flow-builder/upload-samples', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        uploadedFileInfo = uploadResponse.data.processedFiles;
        console.log('📁 Sample files uploaded and processed:', uploadedFileInfo);

        setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === 'user') {
            next[next.length - 1] = {
              ...last,
              attachedFiles: uploadedFileInfo.map(f => f.originalName)
            };
          }
          return next;
        });
      } catch (error) {
        console.error('Error uploading sample files:', error);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Failed to process sample files: ${error.message || 'Unknown error'}. Please try again.`,
          timestamp: new Date()
        }]);
        setLoading(false);
        return;
      }
    }
    
    // If onMessageSend callback is provided (intelligent analysis mode), call it first
    if (onMessageSend) {
      try {
        const analysisResult = await onMessageSend(userMessage, uploadedFileInfo, tagsToSend);
        
        // If analysis returned a result (multi-phase or feature gap), stop here
        // The parent component will handle the UI
        if (analysisResult && (analysisResult.mode === 'multi-phase' || analysisResult.mode === 'feature_gap')) {
          setLoading(false);
          // Reload messages to show what the parent added
          await loadMessages();
          return;
        }
        
        // If analysis returned null or standard mode, continue with normal chat flow
      } catch (error) {
        console.error('Analysis error:', error);
        // On error, fall through to normal chat
      }
    }
    
    // Check if this is a step modification request (mentions specific step like "end step", "last step", etc.)
    // Use the new /modify-step endpoint for more reliable modifications
    console.log('🔧 [ChatPanel] Checking for step modification...', {
      mode,
      hasCurrentFlow: !!currentFlow,
      stepsCount: currentFlow?.steps?.length || 0
    });
    
    if (mode === 'agent' && currentFlow?.steps?.length > 0) {
      console.log('🔧 [ChatPanel] Calling tryStepModification...');
      const stepModificationResult = await tryStepModification(userMessage);
      console.log('🔧 [ChatPanel] tryStepModification result:', stepModificationResult);
      
      if (stepModificationResult) {
        // Modification was applied — user message is already in the list
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: stepModificationResult.message,
          timestamp: new Date(),
          metadata: { action: 'modify-step' }
        }]);
        setLoading(false);
        return;
      }
    } else {
      console.log('🔧 [ChatPanel] Skipping step modification check:', {
        isAgentMode: mode === 'agent',
        hasSteps: currentFlow?.steps?.length > 0
      });
    }
    
    setStreaming(true);
    setStreamingContent('');

    // If this is the first message, update the session name and add instruction (only in Agent mode)
    let messageToSend = userMessage;
    if (isFirstMessage) {
      const title = generateSessionTitle(userMessage);
      updateSessionName(title);
      
      // Add explicit instruction for first message to reinforce JSON output (only in Agent mode)
      if (mode === 'agent') {
        messageToSend = `${userMessage}

CRITICAL REMINDER: You MUST respond with JSON using the EXACT neoConverter FlowStep schema:
- Use "tempId" (not "id")
- Use exact step types: "smart_start", "data_processing", "ai_extraction", "advanced_collect_input", "variable_operations", "end"
- Use specific config fields: "startConfig", "dataProcessingConfig", "aiExtractionConfig", "collectInputConfig", "variableOperationsConfig"
- Root object MUST have "steps" array (not "flow.steps")
- Connect steps with "nextStep" field

DO NOT use: "file_upload", "document_processing", "ai_processing", "data_transformation", "collect_input", "output" - these are WRONG.`;
      }
    }

    try {
      // Use the same API base URL as axios (respects REACT_APP_ENVIRONMENT).
      // Previously this always hit REACT_APP_API_BASE_URL_LOCAL / localhost, so
      // analyze (axios) worked in production while chat streaming silently failed.
      const response = await fetch(`${API_BASE_URL}/ai-flow-builder/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          sessionId: session.sessionId,
          message: messageToSend, // Send augmented message to AI
          stream: true,
          mode: mode, // Pass selected mode
          currentFlowState: currentFlow, // Pass current flow state
          taggedSteps: tagsToSend.length > 0 ? tagsToSend : undefined // User-flagged suspect steps
        })
      });

      if (!response.ok || !response.body) {
        let errorDetail = `Chat request failed (${response.status})`;
        try {
          const errBody = await response.json();
          errorDetail = errBody.message || errBody.error || errorDetail;
        } catch (_) { /* non-JSON error body */ }
        throw new Error(errorDetail);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let sawDone = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));

              if (data.error) {
                throw new Error(data.error);
              }
              
              if (!data.done) {
                if (typeof data.content === 'string' && data.content) {
                  fullContent += data.content;
                  setStreamingContent(fullContent);
                }
              } else {
                sawDone = true;
                // Streaming complete
                const newMessage = {
                  role: 'assistant',
                  content: fullContent,
                  timestamp: new Date(),
                  metadata: data.metadata
                };
                
                // Store flow config in message if this is an agent mode response with flow
                if (mode === 'agent' && data.metadata?.hasFlowConfig) {
                  newMessage.flowConfig = fullContent;
                }
                
                setMessages(prev => [...prev, newMessage]);
                // Clear loading before streaming flags so the thinking bubble doesn't flash
                setLoading(false);
                setStreamingContent('');
                setStreaming(false);

                // Check if response contains flow configuration (only in Agent mode)
                if (mode === 'agent' && data.metadata?.hasFlowConfig) {
                  await checkForFlowConfiguration(fullContent);
                }
              }
            } catch (e) {
              if (e.message && !e.message.includes('JSON')) {
                throw e;
              }
              console.error('Error parsing stream data:', e);
            }
          }
        }
      }

      if (!sawDone && !fullContent) {
        throw new Error('Chat stream ended without a response. Please try again.');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message || 'Please try again.'}`,
        timestamp: new Date()
      }]);
      setStreaming(false);
    } finally {
      setLoading(false);
    }
  };

  const checkForFlowConfiguration = async (content) => {
    const hasPatch = /["']action["']\s*:\s*["']patch_flow["']/.test(content)
      || (/["']modifications["']\s*:\s*\[/.test(content)
        && /["'](?:insert_step|modify_step|replace_operation|update_operation|remove_step)["']/.test(content));
    const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/) ||
                     content.match(/\{[\s\S]*"steps"\s*:\s*\[[\s\S]*\}/);
    
    console.log('🔍 [AI] Checking for flow configuration:', {
      hasMatch: !!(jsonMatch || hasPatch),
      hasPatch,
      contentLength: content.length,
      contentPreview: content.substring(0, 200)
    });
    
    if (jsonMatch || hasPatch) {
      try {
        console.log('✅ [AI] Found actionable JSON in response, calling generate-flow endpoint');
        
        // Parse and generate / patch flow — send full assistant content so
        // the backend can pick the patch_flow block among multiple fences.
        const response = await api.post('/ai-flow-builder/generate-flow', {
          sessionId: session.sessionId,
          aiResponse: content
        });

        console.log('📦 [AI] Generate-flow response:', response.data);

        if (response.data.flow) {
          console.log('🎯 [AI] Calling onFlowGenerated with flow data', {
            appliedPatch: !!response.data.appliedPatch
          });
          // Include both flow (nodes/edges) and steps for proper node configuration
          onFlowGenerated({
            ...response.data.flow,
            steps: response.data.steps // Add step data for configuration panel
          });
          
          // Show change summary if available
          if (response.data.changes) {
            const changes = response.data.changes;
            let summaryParts = [];
            
            if (changes.added && changes.added.length > 0) {
              summaryParts.push(`✅ Added ${changes.added.length} step(s): ${changes.added.join(', ')}`);
            }
            if (changes.modified && changes.modified.length > 0) {
              summaryParts.push(`✏️ Modified ${changes.modified.length} step(s): ${changes.modified.join(', ')}`);
            }
            if (changes.removed && changes.removed.length > 0) {
              summaryParts.push(`❌ Removed ${changes.removed.length} step(s): ${changes.removed.join(', ')}`);
            }
            
            if (summaryParts.length > 0) {
              console.log('📊 Flow changes:', summaryParts.join(' | '));
            }
          }
        } else {
          console.warn('⚠️ [AI] No flow data in response:', response.data);
        }
      } catch (error) {
        console.error('❌ [AI] Error generating flow:', error);
        console.error('Error details:', error.response?.data);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `I prepared a fix but failed to apply it to the canvas: ${error.response?.data?.message || error.message}. Please try again.`,
          timestamp: new Date()
        }]);
      }
    } else {
      console.log('ℹ️ [AI] No flow configuration detected in response');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      sendMessage();
    }
  };

  const handleRegenerateFlow = async (flowConfig) => {
    try {
      console.log('🔄 [AI] Regenerating flow from stored config');
      
      const response = await api.post('/ai-flow-builder/generate-flow', {
        sessionId: session.sessionId,
        aiResponse: flowConfig
      });

      console.log('📦 [AI] Regenerate-flow response:', response.data);

      if (response.data.flow) {
        console.log('🎯 [AI] Calling onFlowGenerated with flow data');
        onFlowGenerated({
          ...response.data.flow,
          steps: response.data.steps
        });
        
        // Show success notification
        console.log('✅ [AI] Flow regenerated successfully');
      }
    } catch (error) {
      console.error('❌ [AI] Error regenerating flow:', error);
      alert('Failed to regenerate flow. Please try again.');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Messages Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: '#f5f5f5'
        }}
      >
        {messages.length === 0 && !loading && !streaming && (
          <Box sx={{ textAlign: 'center', mt: 4, px: 3 }}>
            <AIIcon sx={{ fontSize: 64, color: '#999', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              AI Flow Builder Assistant
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Tell me what you want to build using the Visual Flow Editor.
            </Typography>
            <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 600 }}>
              ⚡ I'll create flow configurations (not code) that appear directly on the canvas!
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
              <Chip 
                label="Example: Build a flow to extract invoice data from PDFs" 
                sx={{ cursor: 'pointer' }}
                onClick={() => setInputMessage("Build a flow to extract invoice data from PDFs")}
              />
              <Chip 
                label="Example: Add a branch to check if amount > 1000" 
                sx={{ cursor: 'pointer' }}
                onClick={() => setInputMessage("Add a branch to check if amount > 1000")}
              />
              <Chip 
                label="Example: Create an API call to send data to Zapier" 
                sx={{ cursor: 'pointer' }}
                onClick={() => setInputMessage("Create an API call to send data to Zapier")}
              />
            </Box>
          </Box>
        )}

        {messages.map((message, index) => {
          console.log(`🔍 [ChatPanel] Message ${index}:`, {
            role: message.role,
            hasFlowConfig: !!message.flowConfig,
            flowConfigLength: message.flowConfig?.length || 0,
            metadata: message.metadata
          });
          return (
            <MessageBubble 
              key={index} 
              message={message} 
              onRegenerate={message.flowConfig ? handleRegenerateFlow : null}
            />
          );
        })}

        {streaming && streamingContent && (
          <MessageBubble 
            message={{
              role: 'assistant',
              content: streamingContent,
              timestamp: new Date(),
              streaming: true
            }}
          />
        )}

        {/* Show while waiting for first tokens (including empty stream) or pre-stream work */}
        {loading && !streamingContent && (
          <ThinkingBubble
            labels={
              streaming
                ? ['Coming up with a response…']
                : ['Thinking…', 'Analyzing your request…', 'Coming up with a response…']
            }
          />
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid #e0e0e0',
          bgcolor: 'white'
        }}
      >
        {/* Mode Selector */}
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode !== null) {
                setMode(newMode);
              }
            }}
            size="small"
            sx={{ height: 32 }}
          >
            <ToggleButton value="agent" sx={{ px: 1.5 }}>
              <Tooltip title="AI will analyze your flow and make changes based on your instructions">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BuildIcon fontSize="small" />
                  <Typography variant="caption" fontWeight={mode === 'agent' ? 600 : 400}>
                    Agent
                  </Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="ask" sx={{ px: 1.5 }}>
              <Tooltip title="Ask questions about your flow without making changes">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" />
                  <Typography variant="caption" fontWeight={mode === 'ask' ? 600 : 400}>
                    Ask
                  </Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            Model: {session.selectedModel}
          </Typography>
        </Box>

        {/* Attached Files Preview */}
        {attachedFiles.length > 0 && (
          <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {attachedFiles.map((file, index) => (
              <Chip
                key={index}
                icon={getFileIcon(file.name)}
                label={file.name.length > 25 ? file.name.substring(0, 22) + '...' : file.name}
                onDelete={() => handleRemoveFile(index)}
                deleteIcon={<CloseIcon fontSize="small" />}
                size="small"
                sx={{
                  bgcolor: '#e3f2fd',
                  '& .MuiChip-deleteIcon': {
                    color: '#666',
                    '&:hover': { color: '#d32f2f' }
                  }
                }}
              />
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
              {attachedFiles.length}/{MAX_FILES} files
            </Typography>
          </Box>
        )}

        {/* Tagged Steps Preview (suspected problem steps) */}
        {taggedSteps.length > 0 && (
          <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              Suspected steps:
            </Typography>
            {taggedSteps.map((step, index) => (
              <Chip
                key={step.id || step.name}
                icon={<TagIcon fontSize="small" />}
                label={step.name.length > 25 ? step.name.substring(0, 22) + '...' : step.name}
                onDelete={() => setTaggedSteps(prev => prev.filter((_, i) => i !== index))}
                deleteIcon={<CloseIcon fontSize="small" />}
                size="small"
                sx={{
                  bgcolor: '#fff3e0',
                  '& .MuiChip-icon': { color: '#e65100' },
                  '& .MuiChip-deleteIcon': {
                    color: '#666',
                    '&:hover': { color: '#d32f2f' }
                  }
                }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept={ACCEPTED_FILE_TYPES}
            multiple
            style={{ display: 'none' }}
          />
          
          {/* Attachment button */}
          <Tooltip title={`Attach sample files (max ${MAX_FILES}, 20MB each) - PDF, Excel, Images, Word, CSV, Text`}>
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || attachedFiles.length >= MAX_FILES}
              sx={{
                color: attachedFiles.length > 0 ? 'primary.main' : 'text.secondary',
                '&:hover': { bgcolor: '#e3f2fd' }
              }}
            >
              <AttachFileIcon />
            </IconButton>
          </Tooltip>

          {/* Tag steps button (suspected problem steps) */}
          <Tooltip
            title={
              getTaggableSteps().length === 0
                ? 'No flow on the canvas yet — build a flow first to tag steps'
                : 'Tag the steps you suspect are causing the issue (or type @ in the message)'
            }
          >
            <span>
              <IconButton
                onClick={(e) => openTagMenu(e.currentTarget, false)}
                disabled={loading || getTaggableSteps().length === 0}
                sx={{
                  color: taggedSteps.length > 0 ? '#e65100' : 'text.secondary',
                  '&:hover': { bgcolor: '#fff3e0' }
                }}
              >
                <TagIcon />
              </IconButton>
            </span>
          </Tooltip>

          {/* Step picker menu (multi-select; stays open until dismissed) */}
          <Menu
            anchorEl={tagMenuAnchor}
            open={Boolean(tagMenuAnchor)}
            onClose={closeTagMenu}
            slotProps={{ paper: { sx: { maxHeight: 320, minWidth: 280 } } }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block' }}>
              Tag suspected problem steps
            </Typography>
            {getTaggableSteps().map((step) => (
              <MenuItem
                key={step.id || step.name}
                dense
                onClick={() => toggleTaggedStep(step)}
              >
                <Checkbox
                  size="small"
                  checked={isStepTagged(step)}
                  sx={{ p: 0.5, mr: 1 }}
                />
                <ListItemText
                  primary={`${step.stepNumber}. ${step.name}`}
                  secondary={step.type}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>
            ))}
          </Menu>
          
          <TextField
            ref={textFieldRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder={
              attachedFiles.length > 0
                ? "Describe what you want to extract from these files... (⌘+Enter to send)"
                : mode === 'agent' 
                  ? "Describe what you want to build or change... @ tags a step (⌘+Enter to send)" 
                  : "Ask a question about your flow... (⌘+Enter to send)"
            }
            value={inputMessage}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            disabled={loading}
            variant="outlined"
            size="small"
          />
          <IconButton
            color="primary"
            onClick={sendMessage}
            disabled={!inputMessage.trim() || loading}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark'
              },
              '&:disabled': {
                bgcolor: '#ccc'
              }
            }}
          >
            {(loading || uploadingFiles) ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Message Bubble Component
 */
const MessageBubble = ({ message, onRegenerate }) => {
  const isUser = message.role === 'user';
  
  console.log(`🔍 [MessageBubble] Rendering:`, {
    role: message.role,
    isUser,
    hasOnRegenerate: !!onRegenerate,
    hasFlowConfig: !!message.flowConfig,
    isStreaming: !!message.streaming
  });

  return (
    <Box
      sx={{
        display: 'flex',
        mb: 2,
        justifyContent: isUser ? 'flex-end' : 'flex-start'
      }}
    >
      {!isUser && (
        <Avatar sx={{ bgcolor: '#1976d2', mr: 1 }}>
          <AIIcon />
        </Avatar>
      )}

      <Paper
        elevation={1}
        sx={{
          p: 2,
          maxWidth: '80%',
          bgcolor: isUser ? '#1976d2' : 'white',
          color: isUser ? 'white' : 'inherit'
        }}
      >
        {isUser ? (
          <Box>
            <Typography variant="body1">{message.content}</Typography>
            {(message.metadata?.taggedSteps?.length > 0) && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {message.metadata.taggedSteps.map((stepName, idx) => (
                  <Chip
                    key={idx}
                    icon={<TagIcon sx={{ color: 'white !important' }} fontSize="small" />}
                    label={stepName}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,152,0,0.35)',
                      color: 'white',
                      '& .MuiChip-icon': { color: 'white' }
                    }}
                  />
                ))}
              </Box>
            )}
            {message.attachedFiles && message.attachedFiles.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {message.attachedFiles.map((fileName, idx) => (
                  <Chip
                    key={idx}
                    icon={<AttachFileIcon sx={{ color: 'white !important' }} fontSize="small" />}
                    label={fileName}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '& .MuiChip-icon': { color: 'white' }
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <ReactMarkdown
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={vscDarkPlus}
                      language={match[1]}
                      PreTag="div"
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                }
              }}
            >
              {message.content}
            </ReactMarkdown>
            
            {message.streaming && (
              <CircularProgress size={16} sx={{ ml: 1, verticalAlign: 'middle' }} />
            )}
            
            {/* Re-execute button for messages with flow configs */}
            {onRegenerate && !message.streaming && (
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => onRegenerate(message.flowConfig)}
                  sx={{ textTransform: 'none' }}
                >
                  Re-apply Flow Changes
                </Button>
              </Box>
            )}
          </Box>
        )}

        <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.7 }}>
          {new Date(message.timestamp).toLocaleTimeString()}
        </Typography>
      </Paper>

      {isUser && (
        <Avatar sx={{ bgcolor: '#666', ml: 1 }}>
          <UserIcon />
        </Avatar>
      )}
    </Box>
  );
};

export default ChatPanel;

